import { Layers, Package2 } from 'lucide-react';
import type { PlanTypeSchema } from '@control-center/shared';

import { PillButton } from '../../../components/clients/PillButton';
import { usePlans } from '../../../hooks/use-plans';
import type { PlanListItem } from '../../../lib/plans-api';

interface Step2PlanProps {
  value: PlanListItem | null;
  onChange: (plan: PlanListItem | null) => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
}

const TYPE_LABEL: Record<PlanTypeSchema, string> = {
  single_saas: 'Single SaaS',
  bundle: 'Bundle',
  service_only: 'Service-only',
};

function formatBRL(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function Step2Plan({
  value,
  onChange,
  onBack,
  onNext,
  onCancel,
}: Step2PlanProps): JSX.Element {
  const list = usePlans({ status: 'ativo', limit: 100 });
  const plans = list.data?.items ?? [];
  const canAdvance = Boolean(value);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="m-0 text-[15px] font-semibold text-ink">Qual plano?</h2>
        <p className="text-[12px] text-muted">Apenas planos ativos aparecem aqui.</p>
      </div>

      {list.isLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              aria-hidden
              className="h-[110px] animate-pulse rounded-[12px] border border-line bg-panel-2"
            />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-line bg-panel-2 p-6 text-center text-[13px] text-muted">
          Nenhum plano ativo cadastrado.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {plans.map((p) => {
            const selected = value?.id === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onChange(p)}
                aria-pressed={selected}
                className={`flex flex-col gap-2 rounded-[12px] border p-4 text-left transition-colors ${
                  selected
                    ? 'border-ink bg-panel-2 ring-2 ring-ink/30'
                    : 'border-line bg-panel hover:bg-panel-2'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 text-muted">
                    <Package2 size={14} strokeWidth={1.8} />
                    <span className="text-[11px] uppercase tracking-wider">
                      {TYPE_LABEL[p.type]}
                    </span>
                  </div>
                  {p.trialDays > 0 && (
                    <span className="rounded-full bg-warn/15 px-2 py-[1px] text-[10px] font-medium text-warn">
                      {p.trialDays}d trial
                    </span>
                  )}
                </div>
                <div className="font-medium text-ink">{p.name}</div>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[14px] font-semibold tnum text-ink">
                      {formatBRL(p.monthlyPrice)}
                    </div>
                    <div className="text-[11px] text-muted-2">por mês</div>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-2">
                    <Layers size={11} strokeWidth={1.8} />
                    {p._count.entitlements} entitlement
                    {p._count.entitlements === 1 ? '' : 's'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onBack}>
          ← Voltar
        </PillButton>
        <div className="flex items-center gap-2">
          <PillButton type="button" onClick={onCancel}>
            Cancelar
          </PillButton>
          <PillButton solid type="button" onClick={onNext} disabled={!canAdvance}>
            Avançar →
          </PillButton>
        </div>
      </div>
    </div>
  );
}
